import {
  Button,
  ButtonGroup,
  MultiChoice,
  Chips,
  DataStatus,
  DialogFooter,
  Form,
  FormSubmitHandler,
  Menu,
  MenuItem,
  NumberInput,
  Separator,
  Slider,
} from 'platform/components';
import {Box, HStack, Show, Space, VStack, Text} from 'platform/foundation';

import {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react';

import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useGetHyundaiServiceInspectionMileagesQuery,
  useGetHyundaiServiceInspectionModelsQuery,
  usePostHyundaiServiceInspectionCreateNewRequestToBasketMutation,
  usePostHyundaiServiceInspectionCreateNewRequestToServiceOrderMutation,
} from '@dms/api/core';
import {useGetVehicleV2Query} from '@dms/api/sales';
import {useGetMetadaServiceCaseQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {Nullish, TestIdProps, suffixTestId} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

interface ExternalPackagesDialogProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId?: string;
  onClose: () => void;
}

const FILTER_BY_VEHICLE = 'VEHICLE';
const FILTER_BY_CUSTOM = 'CUSTOM';

const FILTER_CHIP_OPTIONS = [
  {label: i18n.t('general.labels.vehicle'), value: FILTER_BY_VEHICLE},
  {label: i18n.t('general.labels.custom'), value: FILTER_BY_CUSTOM},
];

const MILEAGE_MIN = 0;
const MILEAGE_MAX = 250_000;
const MILEAGE_DEFAULT_FROM = 50_000;
const MILEAGE_DEFAULT_TO = 150_000;
const MODEL_FILTER_WIDTH = 64;
const MILEAGE_TICKS = [
  {value: 0, label: '0'},
  {value: 50_000, label: '50k'},
  {value: 100_000, label: '100k'},
  {value: 150_000, label: '150k'},
  {value: 200_000, label: '200k'},
  {value: 250_000, label: '250k'},
];
const GRID_CODE = 'hyundai-service-inspections';
const EXTERNAL_FILTER_ID = 'hyundai-service-inspections-external-filter';

const SIDEBAR_MENU_ITEMS: MenuItem[] = [
  {
    id: 'hyundai',
    label: 'Hyundai',
    leftIcon: 'file/folder',
    items: [{id: GRID_CODE, label: 'Hyundai Service inspections'}],
  },
];

type MileageRangeFilter = {
  from?: number;
  to?: number;
};

type ExternalPackagesFilter = {
  mileage?: MileageRangeFilter;
  vinMask?: string;
  model?: string[];
};

type ExternalPackagesQueryFilterObject = QueryFilterObject & {
  mileage?: {
    from?: number | null;
    to?: number | null;
  };
};

type ExternalPackagesFilterConnectorRef = {
  onChange: (filter: ExternalPackagesFilter) => void;
};

interface ExternalPackagesFilterConnectorProps {
  onDataGridFilterChange: (filter: ExternalPackagesFilter) => void;
}

const ExternalPackagesFilterConnector = forwardRef<
  ExternalPackagesFilterConnectorRef,
  ExternalPackagesFilterConnectorProps
>((props, ref) => {
  useImperativeHandle(ref, () => ({onChange: props.onDataGridFilterChange}));
  return <></>;
});

// eslint-disable-next-line complexity -- Pre-existing violation
export function ExternalPackagesDialog(props: ExternalPackagesDialogProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(GRID_CODE);
  const [activeFilterTab, setActiveFilterTab] = useState(FILTER_BY_VEHICLE);
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([]);

  const {
    data: serviceCase,
    isLoading: isServiceCaseLoading,
    isError: isServiceCaseError,
  } = useGetMetadaServiceCaseQuery({serviceCaseId: props.serviceCaseId});

  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleV2Query(
    {vehicleId: serviceCase?.vehicleId ?? ''},
    {skip: isNil(serviceCase?.vehicleId) || activeFilterTab !== FILTER_BY_VEHICLE}
  );

  const {
    data: mileages,
    isLoading: isMileagesLoading,
    isError: isMileagesError,
  } = useGetHyundaiServiceInspectionMileagesQuery(
    {vehicleId: serviceCase?.vehicleId ?? ''},
    {skip: isNil(serviceCase?.vehicleId)}
  );

  const {data: modelsData, isFetching: isModelsFetching} =
    useGetHyundaiServiceInspectionModelsQuery({}, {skip: activeFilterTab !== FILTER_BY_CUSTOM});

  const vinMask = vehicle?.vin ?? '';
  const [mileageFrom, setMileageFrom] = useState<string | Nullish>(null);
  const [mileageTo, setMileageTo] = useState<string | Nullish>(null);
  const [model, setModel] = useState<string[] | Nullish>(null);
  const externalFilterRef = useRef<ExternalPackagesFilterConnectorRef>(null);
  const externalFilterValuesRef = useRef<ExternalPackagesFilter>({});
  const isInitialExternalFilterPushed = useRef(false);

  const fetchedMileageTo =
    !isNil(mileages) && 'mileage_to' in mileages ? mileages.mileage_to : mileages?.milleage_to;
  const resolvedMileageFrom = isNil(mileageFrom)
    ? (mileages?.mileage_from ?? MILEAGE_DEFAULT_FROM).toString()
    : mileageFrom;
  const resolvedMileageTo = isNil(mileageTo)
    ? (fetchedMileageTo ?? MILEAGE_DEFAULT_TO).toString()
    : mileageTo;

  const isWaitingForVehicleFilter =
    activeFilterTab === FILTER_BY_VEHICLE &&
    (isServiceCaseLoading || (!isNil(serviceCase?.vehicleId) && isVehicleLoading));
  const isWaitingForMileageFilter =
    isServiceCaseLoading || (!isNil(serviceCase?.vehicleId) && isMileagesLoading);
  const isVehicleFilterError =
    activeFilterTab === FILTER_BY_VEHICLE &&
    (isServiceCaseError || (!isNil(serviceCase?.vehicleId) && isVehicleError));
  const isMileageFilterError =
    isServiceCaseError || (!isNil(serviceCase?.vehicleId) && isMileagesError);
  const isVehicleFilterReady = activeFilterTab !== FILTER_BY_VEHICLE || isNotNilOrEmpty(vinMask);
  const isMileageFilterReady = isNil(serviceCase?.vehicleId) || !isMileagesLoading;
  const isInitialFilterReady = isVehicleFilterReady && isMileageFilterReady;

  const modelOptions = (modelsData?.model ?? [])
    .filter((m): m is string => isNotNilOrEmpty(m))
    .map((m) => ({label: m, value: m}));

  const buildFilterExtensions = useCallback((): ExternalPackagesFilter => {
    const result: ExternalPackagesFilter = {};

    if (isNotNilOrEmpty(resolvedMileageFrom) || isNotNilOrEmpty(resolvedMileageTo)) {
      result.mileage = {
        from: isNotNilOrEmpty(resolvedMileageFrom) ? Number(resolvedMileageFrom) : undefined,
        to: isNotNilOrEmpty(resolvedMileageTo) ? Number(resolvedMileageTo) : undefined,
      };
    }

    if (activeFilterTab === FILTER_BY_VEHICLE && isNotNilOrEmpty(vinMask)) {
      result.vinMask = vinMask;
    }

    if (activeFilterTab === FILTER_BY_CUSTOM && Array.isArray(model) && model.length > 0) {
      result.model = model;
    }

    return result;
  }, [activeFilterTab, model, resolvedMileageFrom, resolvedMileageTo, vinMask]);

  const queryModifier = useCallback(
    (filter: QueryFilterObject): QueryFilterObject =>
      ({...filter, ...externalFilterValuesRef.current}) as ExternalPackagesQueryFilterObject,
    []
  );

  useEffect(
    function pushFilterExtensionsToDataGrid() {
      const latest = buildFilterExtensions();
      externalFilterValuesRef.current = latest;
      externalFilterRef.current?.onChange(latest);
    },
    [buildFilterExtensions]
  );

  const handleExternalFilterReady = useCallback(() => {
    if (isInitialExternalFilterPushed.current) {
      return;
    }
    isInitialExternalFilterPushed.current = true;
    const latest = buildFilterExtensions();
    externalFilterValuesRef.current = latest;
    externalFilterRef.current?.onChange(latest);
  }, [buildFilterExtensions]);

  const [createNewRequestToBasket, {isLoading: isBasketPending}] =
    usePostHyundaiServiceInspectionCreateNewRequestToBasketMutation();
  const [createNewRequestToServiceOrder, {isLoading: isServiceOrderPending}] =
    usePostHyundaiServiceInspectionCreateNewRequestToServiceOrderMutation();

  const isCreateRequestPending = isBasketPending || isServiceOrderPending;

  const handleSubmit: FormSubmitHandler = () => {
    if (selectedPackageIds.length === 0) {
      return Promise.resolve();
    }

    if (props.serviceOrderId) {
      return createNewRequestToServiceOrder({
        body: {
          inspectionId: selectedPackageIds,
          serviceCaseId: props.serviceCaseId,
          serviceOrderId: props.serviceOrderId,
        },
      })
        .unwrap()
        .then(props.onClose)
        .catch(handleApiError);
    }

    return createNewRequestToBasket({
      body: {
        inspectionId: selectedPackageIds,
        serviceCaseId: props.serviceCaseId,
      },
    })
      .unwrap()
      .then(props.onClose)
      .catch(handleApiError);
  };

  const handleFilterTabChange = (value: string[] | Nullish) => {
    const newTab = value?.[0] ?? FILTER_BY_VEHICLE;
    setActiveFilterTab(newTab);
    if (newTab === FILTER_BY_VEHICLE) {
      setModel(null);
    }
  };

  return (
    <Form isFullHeight onSubmit={handleSubmit}>
      {() => (
        <>
          <HStack spacing={0} align="stretch">
            <Box
              width={92}
              backgroundColor="palettes.neutral.10.100"
              padding={4}
              borderRight="1px solid"
              borderColor="general.separator"
            >
              <Menu
                isSearchable
                isDefaultOpen
                items={SIDEBAR_MENU_ITEMS}
                activeItemId={selectedCategoryId}
                onItemChange={setSelectedCategoryId}
                data-testid={suffixTestId('sidebar', props)}
              />
            </Box>

            <Box flex={1} minWidth={0} padding={4}>
              <VStack spacing={4} align="stretch">
                <HStack spacing={3} align="flex-start">
                  <Box flexShrink={0}>
                    <Chips
                      label={i18n.t('general.labels.filterBy')}
                      options={FILTER_CHIP_OPTIONS}
                      value={[activeFilterTab]}
                      onChange={handleFilterTabChange}
                      isDeselectable
                      data-testid={suffixTestId('filterBy', props)}
                    />
                  </Box>

                  <Box flex={1} minWidth={0}>
                    <VStack spacing={0} align="stretch">
                      <Text size="xSmall" color="secondary">
                        {i18n.t('entity.vehicle.labels.mileage')}
                      </Text>
                      <HStack spacing={3} align="center">
                        <Box width={20}>
                          <NumberInput
                            value={resolvedMileageFrom ? Number(resolvedMileageFrom) : null}
                            decimalPlaces={0}
                            onChange={(value) => setMileageFrom(value?.toString())}
                            data-testid={suffixTestId('mileageFrom', props)}
                          />
                        </Box>
                        <Box flex={1} minWidth={0}>
                          <Slider
                            min={MILEAGE_MIN}
                            max={MILEAGE_MAX}
                            step={5_000}
                            isRange
                            allowMoveStart
                            ticks={MILEAGE_TICKS}
                            value={[
                              resolvedMileageFrom ? Number(resolvedMileageFrom) : MILEAGE_MIN,
                              resolvedMileageTo ? Number(resolvedMileageTo) : MILEAGE_MAX,
                            ]}
                            onChange={(value) => {
                              if (Array.isArray(value)) {
                                setMileageFrom(value[0]?.toString());
                                setMileageTo(value[1]?.toString());
                              }
                            }}
                            data-testid={suffixTestId('mileageSlider', props)}
                          />
                        </Box>
                        <Box width={20}>
                          <NumberInput
                            value={resolvedMileageTo ? Number(resolvedMileageTo) : null}
                            decimalPlaces={0}
                            onChange={(value) => setMileageTo(value?.toString())}
                            data-testid={suffixTestId('mileageTo', props)}
                          />
                        </Box>
                      </HStack>
                    </VStack>
                  </Box>

                  <Show when={activeFilterTab === FILTER_BY_CUSTOM}>
                    <Box width={MODEL_FILTER_WIDTH} minWidth={MODEL_FILTER_WIDTH}>
                      <MultiChoice<string>
                        label={i18n.t('entity.vehicle.labels.model')}
                        options={modelOptions}
                        value={model ?? null}
                        onChange={setModel}
                        isLoading={isModelsFetching}
                        data-testid={suffixTestId('model', props)}
                      />
                    </Box>
                  </Show>
                </HStack>

                <Separator />

                <div id={EXTERNAL_FILTER_ID} />

                <Box minHeight={200} height="100%">
                  <DataStatus
                    isLoading={isWaitingForVehicleFilter || isWaitingForMileageFilter}
                    isError={isVehicleFilterError || isMileageFilterError}
                  >
                    <Show when={isInitialFilterReady}>
                      <DmsDataGrid
                        gridCode={GRID_CODE}
                        autoHeight
                        externalFilterId={EXTERNAL_FILTER_ID}
                        queryModifier={queryModifier}
                        filterComponent={(dataGridProps) => (
                          <ExternalPackagesFilterConnector
                            ref={externalFilterRef}
                            onDataGridFilterChange={dataGridProps.onChange}
                          />
                        )}
                        onExternalFilterChange={handleExternalFilterReady}
                        onRowSelectionChange={(rows) => {
                          const ids = rows
                            .map((row) => row.id as string)
                            .filter((id): id is string => isNotNilOrEmpty(id));
                          setSelectedPackageIds(ids);
                        }}
                        data-testid={suffixTestId('datagrid', props)}
                      />
                    </Show>
                  </DataStatus>
                </Box>

                <Space vertical={4} />
              </VStack>
            </Box>
          </HStack>

          <DialogFooter>
            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discard')}
                variant="secondary"
                onClick={props.onClose}
                data-testid={suffixTestId('discard', props)}
              />
              <Button
                title={i18n.t('entity.orderRequest.actions.createNewRequest')}
                variant="primary"
                type="submit"
                isLoading={isCreateRequestPending}
                isDisabled={selectedPackageIds.length === 0 || isCreateRequestPending}
                data-testid={suffixTestId('createRequest', props)}
              />
            </ButtonGroup>
          </DialogFooter>
        </>
      )}
    </Form>
  );
}
